import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CreditCard, Check, Tag, Trash2, Plus } from "lucide-react";

const PLAN_LABELS = {
  pro: "Pro",
  business: "Business",
  enterprise: "Enterprise",
};

export default function AdminBilling() {
  const [form, setForm] = useState({
    stripe_price_pro_monthly: "",
    stripe_price_pro_yearly: "",
    stripe_price_business_monthly: "",
    stripe_price_business_yearly: "",
    stripe_price_enterprise_monthly: "",
    stripe_price_enterprise_yearly: "",
    stripe_billing_webhook_secret: "",
  });
  const [stripeKeySet, setStripeKeySet] = useState(false);
  const [webhookSecretSet, setWebhookSecretSet] = useState(false);
  const [saving, setSaving] = useState(false);

  // Codes promo
  const [promos, setPromos] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: "", plan: "pro", durationMonths: 12, maxUses: 0, description: "" });
  const [addingPromo, setAddingPromo] = useState(false);
  const [showPromoForm, setShowPromoForm] = useState(false);

  const loadPromos = () => api.get("/admin/promo-codes").then(({ data }) => setPromos(data)).catch(() => {});
  const deletePromo = async (code) => {
    if (!window.confirm(`Supprimer le code ${code} ?`)) return;
    try { await api.delete(`/admin/promo-codes/${code}`); toast.success("Supprimé"); loadPromos(); }
    catch (e) { toast.error("Erreur"); }
  };
  const createPromo = async () => {
    if (!promoForm.code.trim()) return toast.error("Code requis");
    setAddingPromo(true);
    try {
      await api.post("/admin/promo-codes", { ...promoForm, code: promoForm.code.toUpperCase().trim() });
      toast.success("Code créé");
      setPromoForm({ code: "", plan: "pro", durationMonths: 12, maxUses: 0, description: "" });
      setShowPromoForm(false);
      loadPromos();
    } catch (e) { toast.error(e.response?.data?.message || "Erreur"); }
    finally { setAddingPromo(false); }
  };

  useEffect(() => {
    loadPromos();
    api.get("/admin/stripe").then(({ data }) => {
      setStripeKeySet(!!data.stripe_api_key_set);
      setWebhookSecretSet(!!data.stripe_billing_webhook_secret_set);
      setForm((f) => ({
        ...f,
        stripe_price_pro_monthly: data.stripe_price_pro_monthly || "",
        stripe_price_pro_yearly: data.stripe_price_pro_yearly || "",
        stripe_price_business_monthly: data.stripe_price_business_monthly || "",
        stripe_price_business_yearly: data.stripe_price_business_yearly || "",
        stripe_price_enterprise_monthly: data.stripe_price_enterprise_monthly || "",
        stripe_price_enterprise_yearly: data.stripe_price_enterprise_yearly || "",
      }));
    }).catch(() => {});
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.stripe_billing_webhook_secret) delete payload.stripe_billing_webhook_secret;
      await api.put("/admin/stripe", payload);
      toast.success("Configuration Stripe enregistrée");
      if (form.stripe_billing_webhook_secret) {
        setWebhookSecretSet(true);
        setForm((f) => ({ ...f, stripe_billing_webhook_secret: "" }));
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-3xl">
      <div className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Administration</p>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Facturation</h1>
      </div>

      {/* Statut clé Stripe plateforme */}
      <div className="bg-card border border-border rounded-sm p-6 mb-6 space-y-3">
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Stripe — Clé plateforme</h3>
        </div>
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-sm font-semibold border ${
            stripeKeySet ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-muted text-muted-foreground border-transparent"
          }`}>
            {stripeKeySet ? <><Check className="w-3 h-3" /> Configurée</> : "Non configurée"}
          </span>
          <p className="text-sm text-muted-foreground">
            La clé <code className="font-mono text-xs bg-muted px-1 rounded">STRIPE_API_KEY</code> est définie dans le fichier <code className="font-mono text-xs bg-muted px-1 rounded">.env</code> du serveur.
          </p>
        </div>
      </div>

      {/* Price IDs par plan */}
      <div className="bg-card border border-border rounded-sm p-6 space-y-6">
        <div>
          <h3 className="font-heading text-lg font-semibold mb-1">Price IDs Stripe par forfait</h3>
          <p className="text-sm text-muted-foreground">Créez les produits dans votre dashboard Stripe puis collez les IDs ici (<code className="font-mono text-xs">price_xxx</code>).</p>
        </div>

        {["pro", "business", "enterprise"].map((plan) => (
          <div key={plan} className="space-y-3">
            <h4 className="text-sm font-semibold capitalize border-b border-border pb-2">{PLAN_LABELS[plan]}</h4>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Mensuel</Label>
                <Input
                  value={form[`stripe_price_${plan}_monthly`]}
                  onChange={upd(`stripe_price_${plan}_monthly`)}
                  placeholder="price_xxx"
                  className="mt-1.5 rounded-sm font-mono text-xs"
                />
              </div>
              <div>
                <Label>Annuel</Label>
                <Input
                  value={form[`stripe_price_${plan}_yearly`]}
                  onChange={upd(`stripe_price_${plan}_yearly`)}
                  placeholder="price_xxx"
                  className="mt-1.5 rounded-sm font-mono text-xs"
                />
              </div>
            </div>
          </div>
        ))}

        {/* Webhook secret */}
        <div className="pt-4 border-t border-border space-y-3">
          <h4 className="text-sm font-semibold">Secret webhook abonnements</h4>
          <p className="text-sm text-muted-foreground">
            URL du webhook à renseigner dans Stripe : <code className="font-mono text-xs bg-muted px-1 rounded">https://app.acteio.com/api/companies/webhook/billing</code><br />
            Événements à activer : <code className="font-mono text-xs">customer.subscription.*</code>, <code className="font-mono text-xs">invoice.payment_failed</code>
          </p>
          <div>
            <Label>
              Signing secret (whsec_…)
              {webhookSecretSet && <span className="ml-2 text-emerald-600 text-xs font-normal">● Configuré</span>}
            </Label>
            <Input
              type="password"
              value={form.stripe_billing_webhook_secret}
              onChange={upd("stripe_billing_webhook_secret")}
              placeholder={webhookSecretSet ? "Laisser vide pour conserver l'actuel" : "whsec_xxx"}
              className="mt-1.5 rounded-sm"
            />
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
          {saving ? "Enregistrement…" : "Enregistrer la configuration"}
        </Button>
      </div>

      {/* Codes promo */}
      <div className="bg-card border border-border rounded-sm p-6 mt-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-lg font-semibold">Codes promo</h3>
          </div>
          <Button size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => setShowPromoForm(!showPromoForm)}>
            <Plus className="w-3.5 h-3.5" />
            Nouveau code
          </Button>
        </div>

        {showPromoForm && (
          <div className="bg-muted/40 border border-border rounded-sm p-4 space-y-3">
            <h4 className="text-sm font-semibold">Créer un code</h4>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Code *</Label>
                <Input value={promoForm.code} onChange={e => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                  placeholder="CLEYA2026" className="mt-1 rounded-sm font-mono uppercase" />
              </div>
              <div>
                <Label>Forfait accordé</Label>
                <select value={promoForm.plan} onChange={e => setPromoForm({ ...promoForm, plan: e.target.value })}
                  className="mt-1 w-full h-9 rounded-sm border border-input bg-background px-3 text-sm">
                  <option value="pro">Pro</option>
                  <option value="business">Business</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div>
                <Label>Durée (mois, 0 = permanent)</Label>
                <Input type="number" min="0" value={promoForm.durationMonths}
                  onChange={e => setPromoForm({ ...promoForm, durationMonths: parseInt(e.target.value) || 0 })}
                  className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label>Max utilisations (0 = illimité)</Label>
                <Input type="number" min="0" value={promoForm.maxUses}
                  onChange={e => setPromoForm({ ...promoForm, maxUses: parseInt(e.target.value) || 0 })}
                  className="mt-1 rounded-sm" />
              </div>
              <div className="sm:col-span-2">
                <Label>Description (interne)</Label>
                <Input value={promoForm.description} onChange={e => setPromoForm({ ...promoForm, description: e.target.value })}
                  placeholder="Code pour les clients Cleya – 1 an Business" className="mt-1 rounded-sm" />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={createPromo} disabled={addingPromo} className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
                {addingPromo ? "Création…" : "Créer le code"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowPromoForm(false)} className="rounded-sm">Annuler</Button>
            </div>
          </div>
        )}

        {promos.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun code promo.</p>
        ) : (
          <div className="divide-y divide-border">
            {promos.map(p => (
              <div key={p.code} className="py-3 flex items-center gap-3">
                <code className="font-mono font-semibold text-sm min-w-[120px]">{p.code}</code>
                <span className="text-xs px-2 py-0.5 rounded-sm bg-primary/10 text-primary border border-primary/20 font-semibold capitalize">{p.plan}</span>
                <span className="text-xs text-muted-foreground">{p.durationMonths > 0 ? `${p.durationMonths} mois` : 'Permanent'}</span>
                <span className="text-xs text-muted-foreground">{p.usedCount}{p.maxUses > 0 ? `/${p.maxUses}` : ''} use{p.usedCount !== 1 ? 's' : ''}</span>
                {p.description && <span className="text-xs text-muted-foreground flex-1 truncate">{p.description}</span>}
                {!p.description && <span className="flex-1" />}
                <button onClick={() => deletePromo(p.code)} className="text-muted-foreground hover:text-destructive transition-colors ml-auto shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
